export type LpAudience = "general" | "engineer" | "newGrad" | "highSchool";

export interface LpHeroAnimationAxis {
  name: string;
  score: number;
  color: string;
}

export interface LpHeroAnimationCopy {
  questionLabel: string;
  questionText: string;
  axes: LpHeroAnimationAxis[];
  feedbackPositive: string;
  feedbackImprovement: string;
}

export interface LpFeatureItem {
  title: string;
  description: string;
  solution: string;
  image: string;
}

export interface LpStep1Card {
  id: string;
  text: string;
  cat: string;
  active: boolean;
}

export interface LpFaqItem {
  question: string;
  answer: string;
}

export interface LpDocumentFeature {
  /** 見出し。PC では2要素の間で改行し、モバイルでは連結して1行で流す */
  headlineLines: [string, string];
  description: string;
  /** 登録できる応募書類の種別（履歴書・職務経歴書・志望企業 など） */
  documentTypes: string[];
  note: string;
}

export interface LpCopy {
  metadata: {
    title: string;
    description: string;
    canonicalPath: "/" | "/engineer" | "/new-grad" | "/high-school";
  };
  hero: { badge: string };
  feature: {
    headline: string;
    subheadline: string;
    items: LpFeatureItem[];
  };
  howItWorks: {
    step1Description: string;
    step3Description: string;
    step1Cards: LpStep1Card[];
  };
  heroAnimation: LpHeroAnimationCopy;
  faq: LpFaqItem[];
}

const SHARED_FEATURE_ITEM_BASE: Pick<
  LpFeatureItem,
  "image" | "title" | "description"
>[] = [
  {
    image: "/images/top/feat01.png",
    title: "練習相手がいなくても大丈夫",
    description:
      "深夜でも、忙しい合間でも、AIならいつでもあなたの壁打ち相手になります。予約不要、気兼ねなく何度でも。",
  },
  {
    image: "/images/top/feat02.png",
    title: "何がダメだったか、もう迷わない",
    description:
      "「話しすぎ」「具体性に欠ける」など、感覚的な課題をAIが客観的にデータ化。改善すべきポイントが明確になります。",
  },
  {
    image: "/images/top/feat03.png",
    title: "本番で頭が真っ白にならない",
    description:
      "テキストではなく、実際の面接と同じように声で回答。本番の緊張感に近い形で練習できるので、場慣れ効果が得られます。",
  },
];

const featureItems = (solutions: [string, string, string]): LpFeatureItem[] =>
  SHARED_FEATURE_ITEM_BASE.map((base, i) => ({
    ...base,
    solution: solutions[i],
  }));

// 応募書類アップロードによるパーソナライズ訴求。ターゲット非依存のため3バリアント共通で1つだけ定義する。
export const documentFeature: LpDocumentFeature = {
  headlineLines: ["あなたの応募書類に合わせた", "実践的な面接練習"],
  description:
    "履歴書・職務経歴書・面接予定企業を登録すると、AIがあなたの経歴や志望先に踏み込んだ質問を生成します。回答には提出書類の内容を踏まえた、より具体的で詳細なフィードバックが返ります。",
  documentTypes: ["履歴書", "職務経歴書", "面接予定企業"],
  note: "※ ログイン後にご利用いただけます。",
};

export const lpCopy: Record<LpAudience, LpCopy> = {
  general: {
    metadata: {
      title: "MENREN - AIと一緒に、面接を突破しよう",
      description:
        "AIがあなたの面接練習相手。リアルな練習・詳細なフィードバックで、本番の面接力を鍛えます。",
      canonicalPath: "/",
    },
    hero: {
      badge: "面接対策をしたいすべての方へのAI面接コーチ",
    },
    feature: {
      headline: "面接の「困った」を解決します",
      subheadline: "あなたの言語化、磨いてみませんか？",
      items: featureItems([
        "24時間いつでも対応",
        "5軸で回答を客観評価",
        "音声入力で本番と同じ感覚で",
      ]),
    },
    howItWorks: {
      step1Description:
        "面接の頻出質問から、練習したい質問を選びます。「自己紹介をしてください」「あなたの強みは何ですか？」など、面接でよく聞かれる質問で練習できます。何から始めればよいかわからない、という方も、一問一問ステップアップしながら取り組めます。",
      step3Description:
        "AIが「内容の具体性」「論理構成」「伝わりやすさ」「説得力」の観点から、あなたの回答を客観的に分析します。面接のプロの視点をベースにしたフィードバックで、一人でも確実に面接力を高めていけます。",
      step1Cards: [
        {
          id: "Q1",
          text: "自己紹介をしてください。",
          cat: "基本",
          active: false,
        },
        {
          id: "Q2",
          text: "あなたの強みを教えてください。",
          cat: "自己PR",
          active: true,
        },
        {
          id: "Q3",
          text: "なぜ当社を志望しましたか？",
          cat: "志望動機",
          active: false,
        },
      ],
    },
    heroAnimation: {
      questionLabel: "Question 1",
      questionText: "自己紹介をお願いします",
      axes: [
        { name: "論理構造", score: 75, color: "#E95962" },
        { name: "具体性", score: 60, color: "#D4924B" },
        { name: "伝わりやすさ", score: 70, color: "#E95962" },
        { name: "流暢さ", score: 65, color: "#D4924B" },
        { name: "表現力", score: 72, color: "#E95962" },
      ],
      feedbackPositive:
        "結論ファーストで、エピソードを時系列で整理できている点が高評価です。",
      feedbackImprovement:
        "成果や役割を具体的にすると説得力が増します。数値やエピソードを加えましょう。",
    },
    faq: [
      {
        question: "実際にどんな練習ができますか？",
        answer:
          "面接の頻出質問に、実際に声で回答する練習ができます。AIが論理構造・具体性・伝わりやすさなど5つの軸で即座にフィードバックを返すので、何を改善すればいいか明確になります。",
      },
      {
        question: "料金はかかりますか？",
        answer:
          "いいえ、完全無料です。登録不要で今すぐ始められ、ゲストは累計3回まで練習できます。無料登録すると制限なく練習できます。",
      },
      {
        question: "どのような業界・職種に対応していますか？",
        answer:
          "業界・職種を問わず、面接の汎用的な質問に対応しています。エンジニア向け・新卒向けなど、ターゲットに特化したコースもご用意しています。",
      },
      {
        question: "会員登録は必須ですか？",
        answer:
          "1問のみのお試し体験は登録不要で今すぐ開始できます。履歴の保存や詳細な分析には無料の会員登録をおすすめしています。",
      },
    ],
  },

  engineer: {
    metadata: {
      title: "エンジニア向け AI面接コーチ | MENREN",
      description:
        "転職活動中のエンジニア向けAI面接コーチ。技術説明力・具体性など5軸で回答を分析し、本番の面接力を鍛えます。",
      canonicalPath: "/engineer",
    },
    hero: {
      badge: "転職活動中のエンジニアのためのAI面接コーチ",
    },
    feature: {
      headline: "エンジニア面接の「困った」を解決します",
      subheadline: "技術スキルはあっても、それを伝える練習は足りていますか？",
      items: featureItems([
        "24時間いつでも対応",
        "エンジニア特化の評価軸",
        "音声入力で本番と同じ感覚で",
      ]),
    },
    howItWorks: {
      step1Description:
        "エンジニア面接の頻出質問から、練習したい質問を選びます。「自己紹介をしてください」「これまでの開発経験を教えてください」など、面接でよく聞かれる質問で練習できます。何から始めればよいかわからない、という方も、一問一問ステップアップしながら取り組めます。",
      step3Description:
        "エンジニア特化の視点で、AIが改善ポイントを具体的にフィードバックします。「内容の具体性」「論理構成」「技術的な正確さ」「解決力・伝わり方」の5つの観点から、あなたの回答を客観的に分析します。業界エンジニア面接のプロの視点をベースにしたフィードバックで、一人でも確実に面接力を高めていけます。",
      step1Cards: [
        {
          id: "Q1",
          text: "自己紹介をしてください。",
          cat: "基本",
          active: false,
        },
        {
          id: "Q2",
          text: "苦労したプロジェクトについて教えてください。",
          cat: "実績・経験",
          active: true,
        },
        {
          id: "Q3",
          text: "技術選定で重視するポイントは？",
          cat: "技術課題",
          active: false,
        },
      ],
    },
    heroAnimation: {
      questionLabel: "Question 2",
      questionText: "これまでの業務経験を簡潔に教えてください",
      axes: [
        { name: "論理構造", score: 75, color: "#E95962" },
        { name: "具体性", score: 60, color: "#D4924B" },
        { name: "技術説明力", score: 78, color: "#E95962" },
        { name: "流暢さ", score: 65, color: "#D4924B" },
        { name: "表現力", score: 72, color: "#E95962" },
      ],
      feedbackPositive:
        "業務経験を時系列で整理し、具体的な技術スタックに言及できている点が高評価です。",
      feedbackImprovement:
        "成果を数値で示すと説得力が増します。チーム規模や改善率などの定量データを加えましょう。",
    },
    faq: [
      {
        question: "実際にどんな練習ができますか？",
        answer:
          "エンジニア面接の頻出質問に、実際に声で回答する練習ができます。AIが論理構造・技術説明力・具体性など5つの軸で即座にフィードバックを返すので、何を改善すればいいか明確になります。",
      },
      {
        question: "料金はかかりますか？",
        answer:
          "いいえ、完全無料です。登録不要で今すぐ始められ、ゲストは累計3回まで練習できます。無料登録すると制限なく練習できます。",
      },
      {
        question: "どのようなエンジニア職種に対応していますか？",
        answer:
          "フロントエンド、バックエンド、モバイル、インフラ（SRE）、エンジニアリングマネージャーなど、幅広い職種に対応した質問を用意しています。",
      },
      {
        question: "会員登録は必須ですか？",
        answer:
          "1問のみのお試し体験は登録不要で今すぐ開始できます。履歴の保存や詳細な分析には無料の会員登録をおすすめしています。",
      },
    ],
  },

  newGrad: {
    metadata: {
      title: "新卒就活生向け AI面接コーチ | MENREN",
      description:
        "新卒就活生向けAI面接コーチ。自己分析・具体性など5軸で回答を分析し、本番の面接力を鍛えます。",
      canonicalPath: "/new-grad",
    },
    hero: {
      badge: "新卒就活生のためのAI面接コーチ",
    },
    feature: {
      headline: "新卒面接の「困った」を解決します",
      subheadline: "ガクチカも自己PRも、伝え方で評価が変わります。",
      items: featureItems([
        "24時間いつでも対応",
        "新卒就活向けの評価軸",
        "音声入力で本番と同じ感覚で",
      ]),
    },
    howItWorks: {
      step1Description:
        "新卒面接の頻出質問から、練習したい質問を選びます。「自己紹介をしてください」「学生時代に力を入れたことを教えてください」「志望動機を教えてください」など、就活でよく聞かれる質問で練習できます。何から始めればよいかわからない、という方も、一問一問ステップアップしながら取り組めます。",
      step3Description:
        "新卒面接特化の視点で、AIが改善ポイントを具体的にフィードバックします。「内容の具体性」「論理構成」「自己分析の深さ」「伝わりやすさ」の5つの観点から、あなたの回答を客観的に分析します。新卒面接のプロの視点をベースにしたフィードバックで、一人でも確実に面接力を高めていけます。",
      step1Cards: [
        {
          id: "Q1",
          text: "自己紹介をしてください。",
          cat: "基本",
          active: false,
        },
        {
          id: "Q2",
          text: "学生時代に力を入れたことは何ですか？",
          cat: "ガクチカ",
          active: true,
        },
        {
          id: "Q3",
          text: "なぜ当社を志望しましたか？",
          cat: "志望動機",
          active: false,
        },
      ],
    },
    heroAnimation: {
      questionLabel: "Question 2",
      questionText: "学生時代に力を入れたことを教えてください",
      axes: [
        { name: "論理構造", score: 75, color: "#E95962" },
        { name: "具体性", score: 60, color: "#D4924B" },
        { name: "自己分析力", score: 72, color: "#E95962" },
        { name: "流暢さ", score: 65, color: "#D4924B" },
        { name: "表現力", score: 70, color: "#E95962" },
      ],
      feedbackPositive:
        "経験を時系列で整理し、自分の役割を具体的に語れている点が高評価です。",
      feedbackImprovement:
        "学んだことや成果を数値で示すと説得力が増します。役割や規模感を加えましょう。",
    },
    faq: [
      {
        question: "実際にどんな練習ができますか？",
        answer:
          "新卒面接の頻出質問（自己紹介・ガクチカ・志望動機など）に、実際に声で回答する練習ができます。AIが論理構造・自己分析力・具体性など5つの軸で即座にフィードバックを返すので、何を改善すればいいか明確になります。",
      },
      {
        question: "料金はかかりますか？",
        answer:
          "いいえ、完全無料です。登録不要で今すぐ始められ、ゲストは累計3回まで練習できます。無料登録すると制限なく練習できます。",
      },
      {
        question: "どのような業界・職種に対応していますか？",
        answer:
          "業界・職種を問わず、新卒面接の汎用的な質問に対応しています。文系・理系どちらの方にもお使いいただけます。",
      },
      {
        question: "会員登録は必須ですか？",
        answer:
          "1問のみのお試し体験は登録不要で今すぐ開始できます。履歴の保存や詳細な分析には無料の会員登録をおすすめしています。",
      },
    ],
  },

  highSchool: {
    metadata: {
      title: "高校生の総合型選抜向け AI面接コーチ | MENREN",
      description:
        "高校生の総合型選抜・大学入試面接向けAI面接コーチ。志望理由・探究活動・将来像を5軸で分析し、本番の面接力を鍛えます。",
      canonicalPath: "/high-school",
    },
    hero: {
      badge: "総合型選抜に挑む高校生のためのAI面接コーチ",
    },
    feature: {
      headline: "総合型選抜の「伝わらない」を解決します",
      subheadline: "志望理由も探究活動も、自分の言葉で話せるように。",
      items: featureItems([
        "24時間いつでも対応",
        "大学入試面接向けの評価軸",
        "音声入力で本番と同じ感覚で",
      ]),
    },
    howItWorks: {
      step1Description:
        "総合型選抜・大学入試面接で聞かれやすい質問から、練習したい質問を選びます。「志望大学・学部を選んだ理由を教えてください」「探究活動で取り組んだテーマを教えてください」など、高校生向けの質問だけで練習できます。",
      step3Description:
        "大学入試面接に合わせて、AIが改善ポイントを具体的にフィードバックします。「話の構成」「具体性」「志望理由の一貫性」「自己理解」「伝え方」の5つの観点から、あなたの回答を客観的に分析します。",
      step1Cards: [
        {
          id: "Q1",
          text: "志望大学・学部を選んだ理由を教えてください。",
          cat: "志望理由",
          active: false,
        },
        {
          id: "Q2",
          text: "探究活動で取り組んだテーマを教えてください。",
          cat: "探究活動",
          active: true,
        },
        {
          id: "Q3",
          text: "入学後に挑戦したい活動を教えてください。",
          cat: "学びの計画",
          active: false,
        },
      ],
    },
    heroAnimation: {
      questionLabel: "Question 2",
      questionText: "志望理由を教えてください",
      axes: [
        { name: "話の構成", score: 75, color: "#E95962" },
        { name: "具体性", score: 60, color: "#D4924B" },
        { name: "志望理由", score: 78, color: "#E95962" },
        { name: "自己理解", score: 70, color: "#E95962" },
        { name: "伝え方", score: 68, color: "#D4924B" },
      ],
      feedbackPositive:
        "志望分野への関心と高校生活での経験がつながっています。",
      feedbackImprovement:
        "大学で学びたい授業や活動を加えると、志望理由が具体的になります。",
    },
    faq: [
      {
        question: "総合型選抜の面接練習に使えますか？",
        answer:
          "はい。志望理由・探究活動・高校生活で力を入れたこと・入学後に挑戦したいことなど、総合型選抜で聞かれやすい質問で練習できます。",
      },
      {
        question: "料金はかかりますか？",
        answer:
          "いいえ、完全無料です。登録不要で今すぐ始められ、ゲストは累計3回まで練習できます。無料登録すると制限なく練習できます。",
      },
      {
        question: "就活や転職向けの質問も出ますか？",
        answer:
          "高校生向けページから始めると、大学入試面接向けの質問だけが表示されます。企業面接向けの質問は混ざりません。",
      },
      {
        question: "どんなフィードバックが返ってきますか？",
        answer:
          "志望理由の一貫性、探究活動の具体性、学びたいことの明確さ、自己理解、伝え方をもとに、良かった点と次に直すべき点を返します。",
      },
    ],
  },
};

// ──────────────────────────────────────────────────────────────
// 大学キャリアセンター向けLP（#1093）
//
// 消費者向け LP（lpCopy）とはセクション構成が異なるため、専用の型・データを
// 同一ファイル内に分けて定義する。視点（職員視点 / 学生視点）でA/Bするのは
// ヒーロー・課題提起のみで、それ以外のセクションは共通定数として持つ。
// ──────────────────────────────────────────────────────────────

/** A/Bで出し分ける2視点。staff=職員視点（案①）、student=学生視点（案②）。 */
export type CareerCenterAudience = "staff" | "student";

export interface CcHeroCopy {
  badge: string;
  headlineLines: string[];
  /** 1要素が1行。配列の区切りが改行位置になる */
  subheadline: readonly string[];
  /** 正円に収める訴求ポイント。内側の配列の区切りが円内での改行位置になる */
  points: readonly (readonly string[])[];
}

export interface CcProblemCopy {
  /** 課題と解決策をつないだ導入文。1要素が1行で、配列の区切りが改行位置になる */
  lead: readonly string[];
}

export interface CareerCenterCopy {
  metadata: {
    title: string;
    description: string;
    canonicalPath: "/career-center" | "/career-center-student";
  };
  hero: CcHeroCopy;
  problem: CcProblemCopy;
}

export const careerCenterCopy: Record<CareerCenterAudience, CareerCenterCopy> =
  {
    // 案①：キャリアセンター（職員）視点
    staff: {
      metadata: {
        title: "大学キャリアセンター向け AI面接練習｜めんれん先輩",
        description:
          "希望する全学生に、人数の上限なく面接練習の機会を。AIが何度でも模擬面接の相手になり、職員の手を増やさずに学生の面接力向上を支援します。資料請求・無料相談を受付中。",
        canonicalPath: "/career-center",
      },
      hero: {
        badge: "大学キャリアセンターの面接支援に",
        headlineLines: ["学生の数だけ", "面接練習の機会を"],
        subheadline: [
          "希望する全学生に、人数の上限なく練習機会を。",
          "AIが何度でも模擬面接の相手になり、",
          "職員の手を増やさずに一人ひとりの面接力を伸ばします。",
        ],
        // OVERVIEW セクションの3点と内容が重ならないよう、一言で言い切る
        points: [
          ["職員の増員は", "不要"],
          ["全学生に", "同じ機会を"],
          ["練習状況を", "ひと目で"],
        ],
      },
      problem: {
        lead: [
          "就活の過渡期、面接練習の希望は一気に集中します。",
          "限られた職員数で、そのすべてに十分な時間を割くのは簡単ではありません。",
          "AIが何度でも模擬面接の相手になることで、職員の手を増やさずに、",
          "希望する全学生へ練習機会を届けられます。",
        ],
      },
    },

    // 案②：キャリアセンター経由で「学生」を思う視点
    student: {
      metadata: {
        title:
          "学生の面接練習を、待たせない｜めんれん先輩（大学キャリアセンター向け）",
        description:
          "練習したい学生を、待たせないために。AIが何百人分の面接練習にいつでも何度でも付き合います。職員は、本当に支援が必要な学生に集中できます。資料請求・無料相談を受付中。",
        canonicalPath: "/career-center-student",
      },
      hero: {
        badge: "大学キャリアセンターの面接支援に",
        headlineLines: ["いつでもどこでも、", "とことん面接練習をサポート"],
        subheadline: [
          "AIが、学生一人ひとりの面接練習に何度でも付き合います。",
          "深夜でも、思い立ったその瞬間でも。",
          "職員は、本当に支援が必要な学生に集中できます。",
        ],
        // OVERVIEW セクションの3点と内容が重ならないよう、一言で言い切る
        points: [
          ["深夜でも", "練習できる"],
          ["何度でも", "やり直せる"],
          ["伸びが", "数字で見える"],
        ],
      },
      problem: {
        lead: [
          "面接は場数がものを言います。",
          "けれど、練習したいときに相手が見つからず、機会を逃してしまう学生は少なくありません。",
          "深夜でも思い立ったその瞬間でも、AIが一人ひとりの練習に何度でも付き合います。",
          "職員は、本当に支援が必要な学生に集中できます。",
        ],
      },
    },
  };

// ── 以下、両視点で共通のセクション内容 ──

/** 解決策セクション */
export const ccSolution = {
  eyebrow: "OVERVIEW",
  heading: ["AIが、何度でも模擬面接の相手になります"],
  // image は 760×570（4:3）。カードの並び順に ov_01 → ov_03 を割り当てる
  points: [
    {
      title: "人数の上限なく",
      description:
        "予約枠の取り合いは起きません。希望する学生が、好きなときに好きなだけ練習できます。",
      image: "/images/career-center/ov_01.png",
    },
    {
      title: "本番さながらの対話",
      description:
        "AIが回答を深掘りして質問を重ねます。一問一答では終わらない、実践的な練習ができます。",
      image: "/images/career-center/ov_02.png",
    },
    {
      // 旧 RELIABILITY セクション（採点のブレなさ）の内容をここに統合している
      title: "毎回ブレない評価",
      description:
        "回答を複数の観点で採点し、良かった点と次の一歩を具体的に示します。観点の配分と計算は質問の種類ごとに固定しているため、同じ回答なら同じ評価になります。点数の揺れは定期的に検査しています。",
      image: "/images/career-center/ov_03.png",
    },
  ],
} as const;

/** 機能紹介グリッド。status は本番LPでの提供状況の線引きに使う。 */
export const ccFeatures = {
  eyebrow: "FEATURES",
  heading: "主な機能",
  note: "「今後提供」と記載のある機能は、現在開発中の項目です。提供時期は資料請求・無料相談にてご案内します。",
  items: [
    {
      title: "深掘り対話",
      icon: "forum",
      description: "AIが回答を掘り下げ、本番さながらに質問を重ねます。",
      status: "available" as const,
    },
    {
      title: "5つの観点でスコア化",
      icon: "radar",
      description:
        "話の構成・具体性・志望動機の一貫性・自己理解・伝え方を採点します。",
      status: "available" as const,
    },
    {
      title: "ランク判定",
      icon: "workspace_premium",
      description: "総合評価を S / A / B / C のランクでわかりやすく示します。",
      status: "available" as const,
    },
    {
      title: "言い換え例・改善点",
      icon: "edit_note",
      description: "より伝わる回答案と、次に直すべきポイントを提示します。",
      status: "available" as const,
    },
    {
      title: "口ぐせ検出",
      icon: "record_voice_over",
      description: "「えーと」などの無意識のクセを検出して指摘します。",
      status: "available" as const,
    },
    {
      title: "成長の可視化",
      icon: "trending_up",
      description: "練習を重ねるごとのスコア推移をグラフで確認できます。",
      status: "available" as const,
    },
    {
      title: "職員向け管理画面",
      icon: "dashboard",
      description: "学生ごとの練習状況やフィードバックを職員が確認できます。",
      status: "prototype" as const,
    },
    {
      title: "学生への配布リンク",
      icon: "link",
      description: "専用リンクで学生をまとめて利用開始できるようにします。",
      status: "coming" as const,
    },
    {
      title: "グループ管理",
      icon: "groups",
      description: "学部・ゼミ単位で学生をまとめて管理できるようにします。",
      status: "coming" as const,
    },
  ],
} as const;

/** 評価の観点（新卒向け5観点）。態度・表情・感情分析は行わない。 */
export const ccScoring = {
  eyebrow: "EVALUATION",
  heading: "5つの観点で、回答を採点します",
  lead: [
    "新卒就活で問われる力に合わせた観点で採点し、",
    "学生にとっての「次の一歩」を具体的に返します。",
  ],
  axes: [
    {
      name: "話の構成",
      description: "結論から、筋道立てて分かりやすく話せているか。",
    },
    {
      name: "具体性",
      description: "エピソードや事実の裏づけがあり、説得力があるか。",
    },
    {
      name: "志望動機の一貫性",
      description: "なぜその道を選ぶのか、話全体に一貫した軸があるか。",
    },
    {
      name: "自己理解",
      description: "自分の強み・弱みを把握し、自分の言葉で語れているか。",
    },
    {
      name: "伝え方",
      description: "相手に届く話し方ができているか。口ぐせも検出します。",
    },
  ],
  feedbackHeading: "返ってくるフィードバック",
  feedbackItems: [
    "良かった点",
    "改善点と次の一歩",
    "より伝わる言い換え例",
    "話し方のクセ（口ぐせ）の指摘",
  ],
  basicSkillNote:
    "これらの観点は、社会で求められる基礎的な力（前に踏み出す力・考え抜く力・チームで働く力）にもつながります。",
} as const;

/** 採点の信頼性（ブレない）セクション */
export const ccReliability = {
  eyebrow: "RELIABILITY",
  heading: "採点は、毎回ブレません",
  lead: "「AIの採点は気分でブレるのでは？」という不安に応えます。決まった基準と計算で点数を出し、定期的にブレ幅を検査しています。",
  points: [
    {
      title: "質問の種類ごとに、配分を固定",
      description:
        "どの観点をどれだけ重視するかを質問の種類ごとにあらかじめ決めています。担当者の感覚で重みが変わることはありません。",
    },
    {
      title: "決まったロジックで計算",
      description:
        "観点ごとの評価を、決まった計算で総合スコアに落とし込みます。同じ回答なら、同じ基準で評価します。",
    },
    {
      title: "定期的にブレ幅を検査",
      description:
        "同じ回答を繰り返し採点し、点数の揺れが小さく収まっているかを定期的に確認しています。",
    },
  ],
  note: "採点ロジックの詳細は、無料相談・資料請求にてご説明します。",
} as const;

/** 職員向け管理画面セクション */
export const ccAdmin = {
  eyebrow: "FOR STAFF",
  heading: "学生の状況を、職員が一覧で把握できます",
  lead: [
    "誰がどれだけ練習し、どんなフィードバックを受けているか。",
    "職員向け管理画面で確認でき、人による面接指導の効率化につながります。",
  ],
  points: [
    "学生ごとの練習回数・スコア推移を確認できる",
    "個々のフィードバック内容を職員が確認できる",
    "支援が必要な学生の優先順位づけに役立つ",
  ],
  note: "管理画面は現在プロトタイプです。提供時期・本番運用の範囲は無料相談にてご案内します。",
} as const;

/** 導入効果（KPIイメージ） */
export const ccEffect = {
  eyebrow: "IMPACT",
  heading: "導入で目指す効果",
  lead: "練習機会を増やしながら、職員の対応工数を見直します。下記は導入で目指す効果のイメージです。実数値は自校の状況に合わせて試算します。",
  metrics: [
    {
      value: "上限なし",
      label: "練習できる学生数",
      note: "予約枠に縛られない",
    },
    { value: "24時間", label: "練習できる時間帯", note: "深夜・休日でも" },
    { value: "見える化", label: "学生ごとの状況", note: "練習量とスコア推移" },
  ],
  note: "自校での具体的な効果試算は、無料相談にて一緒に行います。",
} as const;

/** 導入の流れ */
export const ccFlow = {
  eyebrow: "FLOW",
  heading: "導入の流れ",
  steps: [
    {
      title: "資料請求・お問い合わせ",
      description: "まずは資料をご請求ください。",
    },
    {
      title: "オンラインでご説明",
      description: "貴学の状況をうかがい、活用イメージをご説明します。",
    },
    { title: "学生へのご案内", description: "学生へ利用方法をご案内します。" },
    { title: "利用開始", description: "学生が面接練習を始められます。" },
  ],
  note: "ご希望に応じて、まず一部の学部・人数で試すパイロット導入のご相談も承ります。",
} as const;

/** よくある質問 */
export const ccFaq = {
  eyebrow: "FAQ",
  heading: "よくある質問",
  items: [
    {
      question: "どんな学生・職種に対応していますか？",
      answer:
        "新卒就活を対象に、文系・理系を問わず幅広く対応しています。自己紹介・ガクチカ・志望動機など、面接でよく問われる質問で練習できます。",
    },
    {
      question: "AIの採点は信頼できますか？",
      answer:
        "質問の種類ごとに評価の配分を固定し、決まったロジックで採点しています。さらに定期的にブレ幅を検査しており、毎回ブレない基準で評価します。詳細は資料・無料相談でご説明します。",
    },
    {
      question: "職員はどのように関わりますか？",
      answer:
        "学生の練習状況やフィードバックを管理画面で確認でき、人による面接指導の効率化に役立ちます。AIが練習量を担い、職員は本当に支援が必要な学生に集中できます。",
    },
    {
      question: "費用感を教えてください。",
      answer:
        "ご利用人数や活用範囲に応じてご案内しています。具体的な料金やパイロット導入の条件は、無料相談にてご説明します。",
    },
    {
      question: "導入実績はありますか？",
      answer:
        "立ち上がったばかりの新しいサービスです。だからこそ、貴学の状況に合わせて一緒に活用方法をつくっていけます。まずはお気軽にご相談ください。",
    },
  ],
} as const;

/** 資料請求フォーム枠の文言 */
export const ccContact = {
  eyebrow: "CONTACT",
  heading: "資料請求",
  lead: [
    "サービス紹介資料をお送りします。",
    "以下のフォームにご記入ください。",
  ],
  // 資料請求（HubSpot フォーム）とは別に、その場で日時を確定できる無料相談への導線を持つ
  consultLead:
    "導入のご相談・自校での効果試算をご希望の方は、オンラインでの無料相談をご予約いただけます。",
  consultLabel: "相談する",
} as const;

/** LP最下部のCTAブロック（グラデーション背景）の見出し */
export const ccFinalCta = {
  heading: "希望する全学生に、練習の機会を",
  lead: [
    "導入のご相談から、自校での効果試算まで承ります。",
    "まずは資料をご覧ください。",
  ],
} as const;

/** 無料相談の日程調整ページの文言 */
export const ccConsult = {
  metaTitle: "無料相談の日程調整 | MENREN キャリアセンター",
  metaDescription:
    "MENREN（めんれん）キャリアセンター向けサービスの無料相談を、オンラインでご予約いただけます。",
  eyebrow: "FREE CONSULTATION",
  heading: "無料相談の日程を選ぶ",
  lead: [
    "導入イメージ・PoC条件・料金などを、オンラインで30〜60分ご説明します。",
    "ご都合のよい日時をお選びください。",
  ],
  backLabel: "← 前のページに戻る",
  steps: [
    { title: "日時を選ぶ", body: "空いている枠から希望の日時を選択します。" },
    {
      title: "情報を入力",
      body: "お名前・大学名・メールを入力して予約を確定します。",
    },
    {
      title: "リンクが届く",
      body: "後日、当日ご利用いただく Google Meetのリンクをお送りします。",
    },
  ],
  bookingCaption: "オンラインで30〜60分",
  meetNote:
    "予約確定後、当日ご利用いただく Google Meetのリンクを後日お送りします。",
} as const;

// ──────────────────────────────────────────────────────────────
// MENREN FOR BUSINESS（企業向け一次面接）LP（#1189）
//
// キャリアセンター向けLPと同じくB2Bリード獲得LPだが、A/Bの出し分けは行わない
// 単一ページのため、セクションごとの共通定数のみで構成する。
// 一次面接機能（#1111系列）はローンチ前のため、コピーは「先行導入企業募集」の
// 枠組みで書き、提供中と誤認させる断定表現を避ける。
// ──────────────────────────────────────────────────────────────

/** メタデータ */
export const bizMeta = {
  title:
    "MENREN for BUSINESS｜AI面接で全応募者と面接し、会うべき人を見逃さない",
  description:
    "御社専用のAI面接官が一次面接を実施。書類だけで判断せず、全応募者に同じ基準の面接を行えます。回答全文と動画で表情・話し方・熱量まで会う前に把握でき、100点満点・5軸で自動採点。先行導入企業を募集中。資料請求・無料相談を受付中。",
  canonicalPath: "/recruit",
} as const;

/** ヒーロー */
export const bizHero = {
  badge: "新卒・中途の一次面接に",
  headlineLines: [
    "AI面接なら、全員と会える。",
    "だから、会うべき人を、見逃さない。",
  ],
  subheadline:
    "MENREN for BUSINESSは、御社専用のAI面接官をつくれる採用支援サービスです。書類だけで判断せず、全応募者に同じ基準の一次面接を実施できます。回答全文と動画で、会う前に人物像まで見極められます。",
} as const;

// 「できること」セクション（BizPossibility）の文章は、改行位置を制御するため
// コンポーネント側に直接持たせている。ここには定義しない。

/**
 * サービス紹介動画（ヒーロー直下）。
 *
 * 動画は「限られた面接枠では全員と会えない」という課題提示から入る構成のため、
 * リード文でも同じ順序をなぞってヒーローの主張を補強する。
 * 動画本体は自ホスティングで、middleware のメディアアクセス制御下にある（同一オリジンのみ許可）。
 */
export const bizPrMovie = {
  eyebrow: "MOVIE",
  heading: "1分で分かるMENREN",
  lead: "限られた面接枠で応募者を絞り込む採用から、全員と面接してから会う人を決める採用へ。MENREN for BUSINESSの仕組みと、担当者が受け取れる評価レポートを約1分の動画で紹介します。",
  src: "/videos/recruit/MENREN_pr_movie.mp4",
  poster: "/images/recruit/pr_movie_poster.webp",
  ariaLabel: "MENREN for BUSINESSのサービス紹介動画",
  fallback: "お使いのブラウザでは動画を再生できません。",
} as const;

/**
 * 4つの強み + 評価レポートのビジュアル。
 *
 * report の評価軸名・スコア・ランクは docs/SCORING_RUBRIC.md の定義に合わせる
 * （5軸／100点満点／ランクは S・A・B・C の4段階。D・E は新規生成しないため使わない）。
 * 総合スコアは axes の平均と一致させ、ランクの閾値（A は 75〜94）と矛盾させない。
 */
export const bizStrengths = {
  eyebrow: "STRENGTHS",
  heading: "全員を漏れなく、正確に見極める4つの強み",
  items: [
    {
      title: "動画で、表情・話し方・熱量まで会う前に分かる",
      description:
        "回答の全文に加えて、面接の様子を動画で確認できます。テキストでは伝わらない人物像まで会う前に把握できるため、書類選考で会うべき人を見逃しません。",
      note: "他のAI面接サービスにはない、MENRENの中核となる強みです。",
    },
    {
      title: "御社専用のAI面接官をつくれる",
      description:
        "求める人物像・質問・評価基準を、自社に合わせて設定できます。社長の姿を模した面接官も用意でき、自社開発は不要です。",
    },
    {
      title: "評価の属人化を、定量評価で解消する",
      description:
        "回答を100点満点・5つの評価軸で採点し、SからCまでの4段階ランクで判定します。面接官ごとのばらつきを、客観的な数値とランクに置き換えられます。なお、スコアはあくまで判断材料で、合否の最終判断は必ず採用担当者が行います。",
    },
    {
      title: "2つの提供方法で、企業規模を問わず導入できる",
      description:
        "自社の採用ページにAI面接の入口を設置する方法と、MENRENの掲載面を使う方法のどちらでも始められます。",
    },
  ],
  report: {
    caption: "評価レポート",
    candidateLabel: "候補者 A",
    rank: "A",
    rankLabel: "総合ランク",
    scoreLabel: "総合スコア",
    score: 82,
    axes: [
      { name: "構造性・論理性", score: 86 },
      { name: "具体性・実績", score: 78 },
      { name: "志望・動機", score: 84 },
      { name: "自己認識・成長", score: 74 },
      { name: "表現・伝達力", score: 88 },
    ],
    summary:
      "結論から簡潔に話せており、質問の意図に沿った回答ができています。プロジェクトでの役割は、面接で具体的に確認することをおすすめします。",
    axisNote: "評価軸と評価基準は、御社の求める人物像に合わせて設定できます。",
    imageNote: "※ 画面はイメージです",
  },
  note: "※ 各機能の提供時期は、資料請求・無料相談にてご案内します。",
} as const;

/**
 * 導入で目指す効果。
 * 面接工数の削減は「見極めた結果として付いてくるもの」として、LP内でこのセクションのみで扱う。
 * 算出根拠は未確定のため、断定せず試算する枠組みで記載する。
 */
export const bizEffect = {
  eyebrow: "IMPACT",
  heading: "導入で目指す効果",
  lead: "全応募者を同じ基準で見極められるようになると、選考そのものが変わります。下記は導入で目指す効果です。",
  items: [
    {
      title: "選考の幅が広がる",
      description:
        "これまで書類だけで判断していた応募者にも会えるようになり、母集団の見極め方が変わります。",
    },
    {
      title: "採用スピードが上がる",
      description:
        "日程調整を待たずに一次面接が進むため、内定までの期間を短縮できます。",
    },
    {
      title: "評価の属人化が解消される",
      description:
        "全応募者が同じ基準で採点され、面接官による評価のばらつきがなくなります。",
    },
    {
      title: "採用ミスマッチが減る",
      description:
        "会う前に人物像まで把握できるため、選考が進んでからの認識の違いを防げます。",
    },
  ],
  metric: {
    value: "最大80%",
    label: "面接工数の削減",
    note: "見極めた結果として付いてくる効果です",
  },
  note: "実際の削減幅は、御社の選考フローに応じて試算します。無料相談にて一緒に確認します。",
} as const;

/** 導入前の不安への回答（応募者体験 + 評価の妥当性・データの取り扱い） */
export const bizTrust = {
  eyebrow: "TRUST",
  heading: "導入前の不安に、先にお答えします",
  lead: "AI面接で気になるのは、応募者の受け止め方と評価の妥当性です。MENREN for BUSINESSは、次の方針で設計しています。",
  points: [
    {
      title: "応募者はログイン不要で、いつでも受験できる",
      description:
        "招待リンクを開くだけで受験でき、会員登録もアプリのインストールも求めません。在職中の転職活動や学業と両立しながら都合のよい時間に受験できるため、日程が合わずに応募を諦められる心配がありません。受験前にはカメラ・マイク・通信状況を確認できます。",
    },
    {
      title: "受験前に、所要時間と流れを提示する",
      description:
        "企業名・ポジション・所要時間・受験の流れを事前に確認できるため、応募者は安心して臨めます。",
    },
    {
      title: "合否の最終判断は、必ず人が行う",
      description:
        "AIの役割はスコアと総評という判断材料を揃えることです。合否の最終判断は、必ず採用担当者が行います。",
    },
    {
      title: "公正な採用選考に配慮する",
      description:
        "適性・能力に関係しない事項を尋ねる設問には、作成時に注意を促し、公正な選考を支えます。",
    },
    {
      title: "応募者データを適切に管理する",
      description:
        "録画や回答のデータは選考の目的に限って利用します。応募者にスコアや合否は表示せず、選考結果のご連絡は企業からの通知に一本化できます。",
    },
  ],
  note: "評価の仕組み・データの取り扱いの詳細は、資料・無料相談にてご説明します。",
} as const;

/** 導入から運用までの流れ（相談 → 設問設計 → 招待 → 評価確認） */
export const bizFlow = {
  eyebrow: "FLOW",
  heading: "導入から運用までの流れ",
  lead: "担当者の作業は、設問の設定と招待だけです。面接の実施から評価まではAIが担います。",
  steps: [
    {
      title: "資料請求・無料相談",
      description:
        "まずは資料のご請求、またはオンラインでの無料相談からどうぞ。",
    },
    {
      title: "活用イメージのすり合わせ",
      description:
        "募集ポジションと選考フローをうかがい、活用方法をご提案します。",
    },
    {
      title: "アカウント発行・設問設計",
      description:
        "アカウントを発行し、求める人物像に合わせた設問と評価基準の設計を一緒に行います。",
    },
    {
      title: "応募者を招待する",
      description:
        "自社の採用ページに受験の入口を置くか、MENRENの掲載面から応募者を受け付けます。",
    },
    {
      title: "評価を確認する",
      description:
        "評価軸ごとのスコアと総評が届きます。回答全文と動画もあわせて確認できます。",
    },
  ],
  note: "MENREN for BUSINESSは現在、先行導入いただける企業を募集しています。提供時期・先行導入の条件は無料相談にてご案内します。",
} as const;

/** よくある質問 */
export const bizFaq = {
  eyebrow: "FAQ",
  heading: "よくある質問",
  items: [
    {
      question: "本当に応募者全員と面接できるのですか？",
      answer:
        "AIが一次面接を実施するため、面接枠の制約を受けません。応募者は都合のよい時間に受験でき、担当者は評価レポートで全員の回答を把握できます。書類の情報だけで会う人を決める必要がなくなります。",
    },
    {
      question: "他のAI面接サービスとの違いは何ですか？",
      answer:
        "回答の全文に加えて、面接の様子を動画で確認できる点です。表情・話し方・熱量といったテキストでは伝わらない情報まで、会う前に把握できます。",
    },
    {
      question: "自社に合わせた質問や評価基準は設定できますか？",
      answer:
        "はい。求める人物像・質問・評価基準を求人ごとに設定でき、設問設計は導入時にサポートします。社長の姿を模した面接官の設定も可能です。",
    },
    {
      question: "応募者にはどのように受験してもらいますか？",
      answer:
        "自社の採用ページにAI面接の入口を設置する方法と、MENRENの掲載面を使う方法の2つがあります。どちらの体制でも始められます。応募者はログイン不要で受験できます。",
    },
    {
      question: "AIが合否を決めるのですか？",
      answer:
        "いいえ。AIはスコアと総評という判断材料を揃える役割で、合否の最終判断は必ず採用担当者が行います。評価の仕組みの詳細は資料・無料相談でご説明します。",
    },
    {
      question: "どんな職種・採用で使えますか？",
      answer:
        "新卒・中途を問わず、幅広い職種の一次選考を想定しています。設問は求人ごとに自由に設定できます。",
    },
    {
      question: "応募者の情報はどのように扱われますか？",
      answer:
        "録画・回答のデータは選考の目的に限って利用し、適切に管理します。応募者にはスコアや合否を表示しません。詳細は資料・無料相談でご説明します。",
    },
    {
      question: "費用感を教えてください。",
      answer:
        "ご利用規模や活用範囲に応じてご案内しています。具体的な料金や先行導入の条件は、無料相談にてご説明します。",
    },
    {
      question: "いつから使えますか？",
      answer:
        "現在、先行導入いただける企業を募集しています。提供時期と導入スケジュールは、無料相談にて個別にご案内します。",
    },
  ],
} as const;

/** 資料請求フォーム枠の文言 */
export const bizContact = {
  eyebrow: "CONTACT",
  heading: "3分でわかるMENRENをダウンロード",
  lead: "MENREN for BUSINESSのサービス紹介資料をお送りします。以下のフォームにご記入ください。",
  // 資料請求（HubSpot フォーム）とは別に、その場で日時を確定できる無料相談への導線を持つ
  consultLead:
    "導入のご相談・活用イメージのすり合わせをご希望の方は、オンラインでの無料相談をご予約いただけます。",
  consultLabel: "無料相談を予約する",
} as const;

/** 無料相談の日程調整ページの文言 */
export const bizConsult = {
  metaTitle: "無料相談の日程調整 | MENREN for BUSINESS",
  metaDescription:
    "MENREN for BUSINESS（AIが一次面接を代行する採用支援サービス）の無料相談を、オンラインでご予約いただけます。",
  eyebrow: "FREE CONSULTATION",
  heading: "無料相談の日程を選ぶ",
  lead: "活用イメージ・先行導入の条件・料金などを、オンラインで30〜60分ご説明します。　ご都合のよい日時をお選びください。",
  backLabel: "← 前のページに戻る",
  steps: [
    { title: "日時を選ぶ", body: "空いている枠から希望の日時を選択します。" },
    {
      title: "情報を入力",
      body: "お名前・会社名・メールを入力して予約を確定します。",
    },
    {
      title: "リンクが届く",
      body: "後日、当日ご利用いただく Google Meetのリンクをお送りします。",
    },
  ],
  bookingCaption: "オンラインで30〜60分",
  meetNote:
    "予約確定後、当日ご利用いただく Google Meetのリンクを後日お送りします。",
} as const;
